import React, { useEffect, useState } from "react";
import { useGame, usePlayer } from "@empirica/core/player/classic/react";
import { Button } from "../components/Button";
import { CompletionLink } from "./CompletionLink";

const API_BASE = import.meta.env.VITE_LLM_PROXY_URL || "";
const API_URL = `${API_BASE}/api/llm-attention-check`;

const CHECK_PROMPT = `What's depicted below?
⠀⠀⠀⠀⠀⢀⡀⠀⠀⠀⠀⠀⡄⠀⠀⠀⠀⢀⠀⠀
⠀⠀⠀⠀⠀⠀⣏⠓⠒⠤⣰⠋⠹⡄⠀⣠⠞⣿⠀⠀
⠀⠀⠀⢀⠄⠂⠙⢦⡀⠐⠨⣆⠁⣷⣮⠖⠋⠉⠁⠀
⠀⠀⡰⠁⠀⠮⠇⠀⣩⠶⠒⠾⣿⡯⡋⠩⡓⢦⣀⡀
⠀⡰⢰⡹⠀⠀⠲⣾⣁⣀⣤⠞⢧⡈⢊⢲⠶⠶⠛⠁
⢀⠃⠀⠀⠀⣌⡅⠀⢀⡀⠀⠀⣈⠻⠦⣤⣿⡀⠀⠀
⠸⣎⠇⠀⠀⡠⡄⠀⠷⠎⠀⠐⡶⠁⠀⠀⣟⡇⠀⠀
⡇⠀⡠⣄⠀⠷⠃⠀⠀⡤⠄⠀⠀⣔⡰⠀⢩⠇⠀⠀
⡇⠀⠻⠋⠀⢀⠤⠀⠈⠛⠁⠀⢀⠉⠁⣠⠏⠀⠀⠀
⣷⢰⢢⠀⠀⠘⠚⠀⢰⣂⠆⠰⢥⡡⠞⠁⠀⠀⠀⠀
⠸⣎⠋⢠⢢⠀⢠⢀⠀⠀⣠⠴⠋⠀⠀⠀⠀⠀⠀⠀
⠀⠘⠷⣬⣅⣀⣬⡷⠖⠋⠁⠀⠀⠀⠀⠀⠀⠀⠀⠀
⠀⠀⠀⠀⠈⠁⠀⠀`;

function formatAttentionCheckError(data, response) {
  const details = data?.debug;
  const parts = [
    data?.error || "Unable to check answer.",
    `HTTP ${response.status}`,
  ];

  if (details?.source) {
    parts.push(`source: ${details.source}`);
  }

  if (details?.code != null) {
    parts.push(`code: ${details.code}`);
  }

  if (details?.type) {
    parts.push(`type: ${details.type}`);
  }

  if (details?.provider) {
    parts.push(`provider: ${details.provider}`);
  }

  if (details?.raw) {
    parts.push(`raw: ${details.raw}`);
  }

  if (details?.metadata) {
    parts.push(`metadata: ${details.metadata}`);
  }

  return parts.join(" | ");
}

function AttentionCheckFailedScreen() {
  return (
    <div className="flex min-h-screen w-full items-start justify-center px-6 py-10">
      <div className="w-full max-w-xl space-y-5 text-center">
        <div>
          <h1 className="text-4xl font-semibold text-gray-900">
            The experiment cannot continue
          </h1>
          <p className="mt-3 text-2xl text-gray-700">
            Please use the completion link below to finish.
          </p>
        </div>
        <CompletionLink unpaid embedded />
      </div>
    </div>
  );
}

export function LlmAttentionCheck({ next }) {
  const game = useGame();
  const player = usePlayer();
  const treatment = game?.get("treatment") || player.get("queueTreatment") || {};
  const design = game?.get("design") || {};
  const testMode = Boolean((treatment.testMode ?? design.testMode) ?? false);
  const savedResult = player.get("llmAttentionCheckResult");
  const [answer, setAnswer] = useState(
    typeof player.get("llmAttentionCheckAnswer") === "string"
      ? player.get("llmAttentionCheckAnswer")
      : ""
  );
  const [isChecking, setIsChecking] = useState(false);
  const [error, setError] = useState("");
  const [failed, setFailed] = useState(savedResult === "fail");

  useEffect(() => {
    if (savedResult === "pass") {
      next();
    }
  }, [next, savedResult]);

  async function submitAnswer(event) {
    event.preventDefault();

    if (isChecking) {
      return;
    }

    const trimmedAnswer = answer.trim();
    if (!trimmedAnswer) {
      setError("Please enter an answer.");
      return;
    }

    setIsChecking(true);
    setError("");
    player.set("llmAttentionCheckAnswer", trimmedAnswer);
    player.set("llmAttentionCheckSubmittedAt", new Date().toISOString());

    try {
      const response = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ answer: trimmedAnswer }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(formatAttentionCheckError(data, response));
      }

      const result = data?.result === "pass" ? "pass" : "fail";
      const resolvedModel = data?.model || "openai/gpt-4.1-mini";
      player.set("llmAttentionCheckResult", result);
      player.set("llmAttentionCheckModel", resolvedModel);
      if (data?.fallbackModel) {
        player.set("llmAttentionCheckFallback", {
          requestedModel: data?.requestedModel || "openai/gpt-4.1-mini",
          fallbackModel: data.fallbackModel,
          at: new Date().toISOString(),
        });
      }
      player.set("llmAttentionCheckCheckedAt", new Date().toISOString());

      if (result === "pass") {
        next();
        return;
      }

      player.set("participantExitStatus", "llm_attention_check_failed");
      player.set("finalPay", 0);
      player.set("removalReason", "llm_attention_check_failed");
      player.set("queueStatus", "attention_check_failed");
      setFailed(true);
    } catch (err) {
      setError(err.message || "Unable to check answer.");
    } finally {
      setIsChecking(false);
    }
  }

  if (failed) {
    return <AttentionCheckFailedScreen />;
  }

  return (
    <form
      className="mx-auto flex min-h-screen w-full max-w-3xl flex-col px-8 py-10"
      onSubmit={submitAnswer}
    >
      <div className="flex-1 space-y-6">
        <h1 className="text-xl font-semibold text-gray-900">
          Please answer the following and then press the "Continue" button.
        </h1>

        <pre className="whitespace-pre overflow-x-auto rounded-md border border-gray-200 bg-white p-5 font-mono text-2xl leading-tight text-gray-900">
          {CHECK_PROMPT}
        </pre>

        <label className="block space-y-2 text-xl font-medium text-gray-700">
          <span>Your answer</span>
          <input
            className="block w-full rounded-md border border-gray-300 px-3 py-2 text-xl font-normal text-gray-900 shadow-sm focus:border-empirica-500 focus:outline-none focus:ring-2 focus:ring-empirica-500"
            value={answer}
            onChange={(event) => setAnswer(event.target.value)}
            autoFocus
            disabled={isChecking}
            maxLength={500}
          />
        </label>

        {error ? (
          <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            {error}
          </div>
        ) : null}
      </div>

      <div className="mt-6 flex items-center gap-3">
        {testMode ? (
          <div data-no-interaction>
            <Button type="button" primary handleClick={next}>
              Bypass page (test)
            </Button>
          </div>
        ) : null}
        <Button type="submit" disabled={isChecking}>
          {isChecking ? "Checking..." : "Continue"}
        </Button>
      </div>
    </form>
  );
}
